import type { DefaultViewReference, DefaultViewScope, ViewScopeType } from "@verbleif/lib/src/composables/Views/DefaultViewReference";
import type { InheritedScopedViewItem } from "@verbleif/shared/src/composables/ScopedViews/types";
import type { RouteLocationRaw } from "vue-router";
import { effectiveDefaultView } from "@verbleif/shared/src/composables/ScopedViews/defaultView.helpers";

/** The URL query parameter each scope type of a view kind is passed with. */
export type ViewScopeQueryKeys = Partial<Record<ViewScopeType, string>>;

/** The list query that opens a default view: its key, plus its scope as the kind's scope parameter. */
export function defaultViewQuery(reference: DefaultViewReference, scopeQueryKeys: ViewScopeQueryKeys): Record<string, string> {
  const query: Record<string, string> = { view: reference.viewKey };
  if (!reference.scope) {
    return query;
  }
  const param = scopeQueryKeys[reference.scope.type];
  if (!param) {
    throw new Error(`Scope type "${reference.scope.type}" has no query parameter for this kind of view`);
  }
  query[param] = String(reference.scope.id);
  if (reference.user !== null) {
    const userParam = scopeQueryKeys.user;
    if (!userParam) {
      throw new Error("A user inside a scope has no query parameter for this kind of view");
    }
    query[userParam] = String(reference.user);
  }
  return query;
}

function firstValue(raw: unknown): string | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * The scope a list query opens its view with. A query with more than one scope parameter cannot be
 * stored as one default view, so it has none; only a user inside a task list (department) pairs up
 * with it (`task_list@taskList:3,user:41`).
 */
export function scopeFromViewQuery(query: Record<string, unknown>, scopeQueryKeys: ViewScopeQueryKeys): DefaultViewScope | null {
  const scopes: DefaultViewScope[] = [];
  for (const [type, param] of Object.entries(scopeQueryKeys) as [ViewScopeType, string][]) {
    const value = firstValue(query[param]);
    if (value === null) {
      continue;
    }
    const id = Number(value);
    if (!Number.isSafeInteger(id) || id < 1) {
      return null;
    }
    scopes.push({ type, id });
  }
  if (scopes.length === 2) {
    const taskList = scopes.find(scope => scope.type === "taskList");
    const user = scopes.find(scope => scope.type === "user");
    return taskList && user ? { type: "taskList", id: taskList.id, user: user.id } : null;
  }
  return scopes.length === 1 ? scopes[0] : null;
}

/** The scope of a sidebar link that opens a list. */
export function scopeFromViewLink(to: RouteLocationRaw | string | undefined, scopeQueryKeys: ViewScopeQueryKeys): DefaultViewScope | null {
  if (!to || typeof to === "string" || !("query" in to) || !to.query) {
    return null;
  }
  return scopeFromViewQuery(to.query as Record<string, unknown>, scopeQueryKeys);
}

function hasScopeParam(query: Record<string, unknown>, scopeQueryKeys: ViewScopeQueryKeys): boolean {
  return Object.values(scopeQueryKeys).some(param => firstValue(query[param]) !== null);
}

/**
 * The view (and scope) a list opens on when the URL names no view: the user's default view as the
 * API resolved it. A scope already in the URL is explicit, so the default's own scope is not added
 * to it. `fallbackKey` is only used when the effective views could not be loaded.
 */
export function startViewQuery(
  views: readonly InheritedScopedViewItem[],
  query: Record<string, unknown>,
  scopeQueryKeys: ViewScopeQueryKeys,
  fallbackKey: string,
): Record<string, string> {
  const reference = effectiveDefaultView(views);
  if (!reference) {
    return { view: fallbackKey };
  }
  if (hasScopeParam(query, scopeQueryKeys)) {
    return { view: reference.viewKey };
  }
  return defaultViewQuery(reference, scopeQueryKeys);
}
